import {
  UseQueryOptions,
  UseQueryResult,
  useQueries,
  useQuery,
} from 'react-query';
import {
  Connection as StardogConnection,
  query as StardogQuery,
} from 'stardog';

import {
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_GRAPH_ALIASES_IRI,
  STARDOG_GRAPH_ALIAS_IRI,
  STARDOG_SQL_SCHEMA_IRI,
} from 'bones/constants/iris';
import { VoiceboxNamedGraphs } from 'bones/constants/voicebox/settings';
import { runWithRefetchConnectionToken } from 'bones/utils/portal/refetchConnectionToken';
import { useStardogConnectionFromPortalIndex } from 'bones/utils/portal/useStardogConnectionFromPortalIndex';

const getNamedGraphQuery = (filter?: string) => `\
# Voicebox Named Graphs
SELECT DISTINCT
  ?graph
  ?isAlias
  ?alias
FROM NAMED <${STARDOG_CONTEXT_ALL_IRI}>
WHERE {
  {
    GRAPH ?graph { }
    FILTER ( ?graph NOT IN ( <${STARDOG_GRAPH_ALIASES_IRI}>, <${STARDOG_SQL_SCHEMA_IRI}> ) )
    BIND(false as ?isAlias)
  } UNION {
    GRAPH <${STARDOG_GRAPH_ALIASES_IRI}> {
      ?graph <${STARDOG_GRAPH_ALIAS_IRI}> ?alias
    }
    BIND(true as ?isAlias)
  }${
    filter
      ? `\
  FILTER (CONTAINS(LCASE(STR(?graph)), LCASE('${filter}')))`
      : ''
  }
}
LIMIT 1000`;

const requestPortalVoiceboxNamedGraphs =
  (
    stardogConnection: StardogConnection | null,
    connectionIdx: number,
    databaseId: string,
    filter?: string
  ) =>
  async () => {
    if (!stardogConnection || !databaseId) {
      return null;
    }

    const response = await runWithRefetchConnectionToken(
      stardogConnection,
      connectionIdx,
      (stardogConn) =>
        StardogQuery.execute(
          stardogConn,
          databaseId,
          getNamedGraphQuery(filter)
        )
    );

    if (!response.ok) {
      throw new Error(response.statusText || String(response.status));
    }

    const bindings: Array<{
      graph?: { value?: string };
      isAlias?: { value?: string };
      alias?: { value?: string };
    }> = response.body?.results?.bindings || [];

    const namedGraphs = bindings.reduce<VoiceboxNamedGraphs>((acc, binding) => {
      const iri = binding?.graph?.value;
      if (!iri) {
        return acc;
      }

      if (!acc[iri]) {
        acc[iri] = {
          iri,
          aliasIris: [],
          isAlias: false,
          isVirtual: iri.startsWith('virtual://'),
        };
      }

      if (binding.isAlias?.value === 'true') {
        acc[iri].isAlias = true;
      }

      if (binding.alias?.value) {
        acc[iri].aliasIris.push(binding.alias.value);

        if (binding.alias.value.startsWith('virtual://')) {
          acc[iri].isVirtual = true;
        }
      }

      return acc;
    }, {});

    return { databaseId, namedGraphs };
  };

export const usePortalVoiceboxDatabaseNamedGraphs = (
  connectionIdx: number,
  databaseId: string,
  filter: string,
  options?: UseQueryOptions<{
    databaseId: string;
    namedGraphs: VoiceboxNamedGraphs;
  } | null>
): UseQueryResult<{
  databaseId: string;
  namedGraphs: VoiceboxNamedGraphs;
} | null> => {
  const stardogConnection = useStardogConnectionFromPortalIndex(connectionIdx);
  return useQuery<{
    databaseId: string;
    namedGraphs: VoiceboxNamedGraphs;
  } | null>(
    `usePortalVoiceboxNamedGraphs-${
      stardogConnection ? connectionIdx : null
    }-${databaseId}${filter ? `-${filter}` : ''}`,
    requestPortalVoiceboxNamedGraphs(
      stardogConnection,
      connectionIdx,
      databaseId,
      filter
    ),
    options
  );
};

export const usePortalVoiceboxDatabasesNamedGraphs = (
  connectionIdx: number,
  databaseIds: string[],
  options?: (
    ...args: Parameters<typeof requestPortalVoiceboxNamedGraphs>
  ) => UseQueryOptions<{
    databaseId: string;
    namedGraphs: VoiceboxNamedGraphs;
  } | null>
): Array<
  UseQueryResult<{
    databaseId: string;
    namedGraphs: VoiceboxNamedGraphs;
  } | null>
> => {
  const stardogConnection = useStardogConnectionFromPortalIndex(connectionIdx);
  return useQueries(
    databaseIds.map(
      (
        databaseId
      ): UseQueryOptions<{
        databaseId: string;
        namedGraphs: VoiceboxNamedGraphs;
      } | null> => ({
        queryKey: `usePortalVoiceboxNamedGraphs-${
          stardogConnection ? connectionIdx : null
        }-${databaseId}`,
        queryFn: requestPortalVoiceboxNamedGraphs(
          stardogConnection,
          connectionIdx,
          databaseId
        ),
        ...(options?.(stardogConnection, connectionIdx, databaseId) || {}),
      })
    )
  );
};
